import _ from 'lodash'

import type { GroupingMetadata } from '@/components/lemmala-design-system/table/types'

/**
 * Restores the visual grouping hierarchy after AG Grid reorders rows during sorting.
 *
 * AG Grid's sort operates on all rows independently, which can scatter group headers
 * and children anywhere in the list. This function re-attaches each group header
 * and its children directly after their parent row.
 *
 * Works on plain data objects, making it easy to test without AG Grid internals.
 */
export function reorderGroupableRows<T extends GroupingMetadata & { id?: string }>(rows: T[]): T[] {
  // Split rows by role:
  // - group headers: the "N items" rows that expand/collapse children
  // - children: grouped rows belonging to a parent, hidden until the group header is expanded
  // - parents: top-level / standalone rows
  const [groupHeaders, nonGroupHeaders] = _.partition(rows, (r) => r.isGroupHeader && r.groupId)
  const [children, parents] = _.partition(nonGroupHeaders, (r) => r.isGroupChild && r.groupId)

  // Index group headers and children by groupId
  const groupHeaderByGroupId = _.keyBy(groupHeaders, 'groupId')
  const childrenByGroupId = _.groupBy(children, 'groupId')

  // Reassemble: each parent is followed by its group header and children
  return parents.flatMap((row) => {
    const groupHeader = row.id !== undefined ? groupHeaderByGroupId[row.id] : undefined
    return groupHeader ? [row, groupHeader, ...(childrenByGroupId[row.id!] ?? [])] : [row]
  })
}
